import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usersAPI, tenantsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Trash,
  CircleNotch,
  Users,
  UserCircle
} from '@phosphor-icons/react';

export default function UsersPage() {
  const { isSuperadmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    tenant_id: '',
  });

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantsAPI.getAll();
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, []);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await usersAPI.create(newUser);
      toast.success('User created successfully');
      setCreateOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'viewer', tenant_id: '' });
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === currentUser?.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    
    if (!window.confirm(`Delete user "${userName}"?`)) return;

    try {
      await usersAPI.delete(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const roleOptions = isSuperadmin 
    ? ['superadmin', 'admin', 'technician', 'viewer']
    : ['technician', 'viewer'];

  return (
    <div data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Users</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">
            {users.length} user{users.length !== 1 ? 's' : ''} registered
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn" className="bg-[#D50C2D] hover:bg-[#f21d41]">
          <Plus size={18} />
          <span>Add User</span>
        </Button>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircleNotch size={32} className="animate-spin text-[#D50C2D]" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 bg-[#141416] border border-white/10 rounded">
          <Users size={48} className="mx-auto text-[#52525b] mb-4" />
          <p className="text-[#a1a1aa]">No users found</p>
        </div>
      ) : (
        <div className="bg-[#141416] border border-white/10 rounded overflow-hidden">
          <table className="data-table" data-testid="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Tenant</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-[#1C1C1F] flex items-center justify-center text-[#D50C2D] font-heading font-bold text-sm">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <span className="font-medium text-white">{user.name}</span>
                    </div>
                  </td>
                  <td className="text-[#a1a1aa]">{user.email}</td>
                  <td><span className={`role-badge ${user.role}`}>{user.role}</span></td>
                  <td><span className="tenant-badge">{user.tenant_name || '-'}</span></td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'online' : 'offline'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {isSuperadmin && user.id !== currentUser?.id && (
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        data-testid={`delete-user-${user.id}`}
                        className="p-2 text-[#f87171] hover:bg-red-500/10 rounded transition-colors"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#a1a1aa]">Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                className="bg-[#0A0A0B] border-white/10 mt-1"
              />
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="bg-[#0A0A0B] border-white/10 mt-1"
              />
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                className="bg-[#0A0A0B] border-white/10 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#a1a1aa]">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10">
                    {roleOptions.map(role => (
                      <SelectItem key={role} value={role} className="capitalize">{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isSuperadmin && (
                <div>
                  <Label className="text-[#a1a1aa]">Tenant</Label>
                  <Select value={newUser.tenant_id} onValueChange={(v) => setNewUser(prev => ({ ...prev, tenant_id: v }))}>
                    <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141416] border-white/10">
                      {tenants.map(tenant => (
                        <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
